import { Lock, Paperclip } from "lucide-react";

import type { Attachment, TimelineItem } from "../api/types";
import { formatDateTime } from "../lib/format";
import { Avatar } from "../ui/Avatar";
import { attachmentUrl } from "./api";
import { describeEvent, formatBytes } from "./labels";

export function AttachmentLinks({ reference, attachments }: { reference: string; attachments: Attachment[] }) {
  if (!attachments.length) return null;
  return (
    <ul className="attachment-links">
      {attachments.map((a) => (
        <li key={a.id}>
          <a className="file-chip" href={attachmentUrl(reference, a.id)} download={a.filename}>
            <Paperclip size={14} aria-hidden="true" />
            <span className="file-chip-name">{a.filename}</span>
            <span className="muted">{formatBytes(a.size_bytes)}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function reasonOf(item: TimelineItem): string | null {
  const reason = item.data.reason;
  return typeof reason === "string" && reason && !item.data.automatic ? reason : null;
}

export function Timeline({ reference, items }: { reference: string; items: TimelineItem[] }) {
  if (!items.length) return null;
  return (
    <ol className="timeline" aria-label="Ticket history">
      {items.map((item) =>
        item.kind === "comment" ? (
          <li key={item.id} className={item.is_internal ? "message message-internal" : "message"}>
            <Avatar name={item.actor?.name ?? "?"} seed={item.actor?.id ?? "?"} size="sm" />
            <div className="message-body">
              <div className="message-meta">
                <span className="cell-strong">{item.actor?.name ?? "Someone"}</span>
                {item.is_internal ? (
                  <span className="internal-label">
                    <Lock size={12} aria-hidden="true" />
                    Internal note
                  </span>
                ) : null}
                <time className="muted" dateTime={item.at}>
                  {formatDateTime(item.at)}
                </time>
              </div>
              <p className="message-text">{item.body}</p>
              {item.mentions.length ? (
                <p className="muted message-mentions">Mentioned {item.mentions.map((m) => m.name).join(", ")}</p>
              ) : null}
              <AttachmentLinks reference={reference} attachments={item.attachments} />
            </div>
          </li>
        ) : item.kind === "attachment_added" ? null : (
          <li key={item.id} className={item.is_internal ? "event event-internal" : "event"}>
            <span className="event-dot" aria-hidden="true" />
            <div>
              <span>
                <span className="cell-strong">{item.actor?.name ?? "Support desk"}</span> {describeEvent(item)}
              </span>{" "}
              <time className="event-time" dateTime={item.at}>
                {formatDateTime(item.at)}
              </time>
              {reasonOf(item) ? <p className="event-reason">"{reasonOf(item)}"</p> : null}
            </div>
          </li>
        ),
      )}
    </ol>
  );
}
